"use client";

import React from "react";
import { Stamp, Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
function SignatureRequestForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="125987" className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in duration-500">
        <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center text-primary mb-4">
          <CircleCheck className="size-8" />
        </div>
        <h3 className="text-2xl font-sans font-bold text-foreground tracking-tight">Inquiry Transmitted</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/10 text-primary">
          Submit New Inquiry
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Contact Name</FormLabel>
              <FormControl>
                <Input placeholder="Alex Chen" className="border-border/50 focus-visible:ring-primary/50 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email Address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="a.chen@enterprise.com" className="border-border/50 focus-visible:ring-primary/50 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Direct Line <span className="text-muted-foreground/50 lowercase tracking-normal">(optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+86 10-1234-5678" className="border-border/50 focus-visible:ring-primary/50 bg-background/50" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="item" rules={{
        required: "Please select a solution category"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Solution Category</FormLabel>
              <FormControl>
                <div className="relative">
                  <select className="flex h-9 w-full rounded-md border border-border/50 bg-background/50 px-3 py-1 text-base shadow-xs transition-colors focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-primary/50 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm appearance-none" {...field}>
                    <option value="" disabled className="bg-popover text-muted-foreground">Select a category</option>
                    {["Optical Fiber", "AI Data Center", "FPV Systems", "Custom Integration"].map((opt, i) => <option key={i} data-index={i} value={opt} className="bg-popover text-foreground">
                        {opt}
                      </option>)}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
                    <svg className="size-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                      <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                    </svg>
                  </div>
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="personalization_details" rules={{
      required: "Project requirements are required",
      maxLength: {
        value: 30,
        message: "Maximum 30 characters allowed"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Project Summary / Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Briefly describe your technical specifications or infrastructure needs..." className="min-h-[100px] resize-none border-border/50 focus-visible:ring-primary/50 bg-background/50" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70 flex justify-between font-mono">
              <span>Maximum 30 characters for the initial brief.</span>
              <span>{field.value?.length || 0}/30</span>
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="desired_delivery_date" render={({
      field
    }) => <FormItem className="md:w-1/2">
            <FormLabel className="text-muted-foreground font-mono uppercase tracking-widest text-xs">Requested Project Timeline <span className="text-muted-foreground/50 lowercase tracking-normal">(optional)</span></FormLabel>
            <FormControl>
              <Input type="date" className="border-border/50 focus-visible:ring-primary/50 block w-full bg-background/50" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-muted-foreground/70 font-mono">
              Select an estimated deployment or delivery date.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm text-center font-mono">{submitError}</p>
        </div>}

      <div className="pt-6 flex justify-center">
        <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto min-w-[240px] bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 font-sans font-bold uppercase tracking-widest text-xs h-12 shadow-[0_0_20px_rgba(180,100%,50%,0.3)]">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-4" />
              Transmitting...
            </> : "Transmit Request"}
        </Button>
      </div>
    </Form>;
}
export default function TheSignatureRequest() {
  return <section id="inquire-about-solutions" className="dark bg-background text-foreground py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,var(--color-primary)_0%,transparent_70%)] opacity-10 pointer-events-none" />

      <div className="max-w-3xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-sans font-bold text-foreground mb-4 tracking-tight uppercase">
            Inquire About Solutions
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto font-sans leading-relaxed">
            Initiate a technical consultation for your infrastructure or aerial systems. Our engineers will review your requirements for precision-engineered solutions.
          </p>
        </div>

        <div className="relative mt-12">
          {}
          <div className="absolute -top-8 left-1/2 -translate-x-1/2 z-20">
            <div className="bg-background p-1.5 rounded-full border border-primary/30 shadow-lg">
              <div className="bg-primary text-primary-foreground p-3 rounded-full shadow-md flex items-center justify-center relative overflow-hidden group">
                <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity" />
                <Stamp className="size-6 relative z-10" />
              </div>
            </div>
          </div>

          {}
          <div className="bg-card/40 backdrop-blur-md border border-primary/20 rounded-xl p-8 md:p-12 pt-16 shadow-2xl relative">
            {}
            <div className="absolute top-0 left-0 size-6 border-t-2 border-l-2 border-primary/40 rounded-tl-xl -translate-x-[1px] -translate-y-[1px]" />
            <div className="absolute top-0 right-0 size-6 border-t-2 border-r-2 border-primary/40 rounded-tr-xl translate-x-[1px] -translate-y-[1px]" />
            <div className="absolute bottom-0 left-0 size-6 border-b-2 border-l-2 border-primary/40 rounded-bl-xl -translate-x-[1px] translate-y-[1px]" />
            <div className="absolute bottom-0 right-0 size-6 border-b-2 border-r-2 border-primary/40 rounded-br-xl translate-x-[1px] translate-y-[1px]" />

            <FormProvider formKey="inquiry_form_solutions" formId="inquiry_form_solutions" sectionName="Inquire About Solutions" defaultValues={{
            full_name: "",
            email: "",
            phone: "",
            item: "",
            personalization_details: "",
            desired_delivery_date: ""
          }} validationRules={{
            full_name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            item: {
              required: true
            },
            personalization_details: {
              required: true,
              maxLength: 30
            }
          }} submitText="Transmit Request" successMessage="Your inquiry has been encrypted and transmitted to our technical department. A solutions architect will contact you within 24 hours.">
              <SignatureRequestForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}